import { memo, useEffect, useState } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, Text, Table, Button, Heading } from '@frontend/design-system';
import { AccordionId, useAccordionContext } from '../../context/AccordionContext';
import { Benefit } from '../../types/insurance-report-api.types';
import { FREQUENCY_LIMITATION_COLUMNS_CONFIG } from '../../utils/constants';
import { prepareLimitationData } from '../../utils/table-helpers';

type AccordionTableProps = {
  accordionTitle: string;
  accordionId: AccordionId;
  data: Benefit[];
  accordionCoveragePercentage?: string;
  customStyles?: SerializedStyles;
  isFavoriteCDTTable?: boolean;
  customHeaderStyles?: SerializedStyles;
  customTableStyles?: SerializedStyles;
  onManageFavorites?: () => void;
};

export const AccordionTable = memo(
  ({
    accordionTitle,
    accordionCoveragePercentage,
    accordionId,
    data,
    customStyles,
    isFavoriteCDTTable,
    customHeaderStyles,
    customTableStyles,
    onManageFavorites,
  }: AccordionTableProps) => {
    const { t } = useTranslation('insurance-eligibility-report');
    const { activeAccordionIds, toggleAccordion } = useAccordionContext();
    const isActive = activeAccordionIds.has(accordionId);

    const [tableData, setTableData] = useState<Benefit[]>(data || []);

    const handleAccordionChange = () => {
      toggleAccordion(accordionId);
    };

    /**
     * @desc: Get the value of a specific column for a given row
     * @note This method is specifically created to handle custom sorting for columns and to get the value of a column
     */
    const getColumnValue = (row: Benefit, columnId: string) => {
      switch (columnId) {
        case 'limitation':
          return prepareLimitationData(row);
        default:
          return (row as any)[columnId] || '';
      }
    };

    /**
     * @desc : Handle sorting of table data based on column and direction
     * @note : This method is specifically created to handle custom sorting for columns
     */
    const handleSorting = (columnId: string, sortDirection: 'asc' | 'desc') => {
      const sortedTableData = [...tableData].sort((a, b) => {
        const aValue = getColumnValue(a, columnId) ?? '';
        const bValue = getColumnValue(b, columnId) ?? '';

        // Handle special values (`-`, `null`, empty string) first.
        const aHasEmptyValue = !aValue || aValue === '-' || aValue === '';
        const bHasEmptyValue = !bValue || bValue === '-' || bValue === '';

        // If 'a' is empty and 'b' is not empty, 'a' goes to the bottom.
        if (aHasEmptyValue && !bHasEmptyValue) {
          return 1;
        }
        // If 'b' is empty and 'a' is not empty, 'b' goes to the bottom.
        if (!aHasEmptyValue && aHasEmptyValue) {
          return -1;
        }

        // standard comparison for non-empty values
        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }

        // values are equal
        return 0;
      });
      setTableData(sortedTableData);
    };

    // Update table data when the data prop changes
    useEffect(() => {
      setTableData(data);
    }, [data]);

    return (
      <Accordion
        css={customStyles}
        onChange={handleAccordionChange}
        padding={[0, 0, 0, 2]}
        chevronSize={16}
        controlledValue={isActive ? accordionId : null}
      >
        <Accordion.Item css={[accordionItemStyles, customStyles]} value={accordionId}>
          <Accordion.Header css={[headerStyles, customHeaderStyles]}>
            <div className='header-container'>
              <div className='accordion-header'>
                {isFavoriteCDTTable ? (
                  <>
                    <Icon name='star-filled-small' color='warn' />
                    <Heading level={3} css={{ lineHeight: theme.spacing(3) }}>
                      {accordionTitle}
                    </Heading>
                  </>
                ) : (
                  <>
                    <Text size='large' weight='bold'>
                      {accordionTitle}
                    </Text>
                    <Text weight='light'>{accordionCoveragePercentage}</Text>
                  </>
                )}
              </div>
              {isFavoriteCDTTable && (
                <Button
                  variant='tertiary'
                  onClick={() => {
                    onManageFavorites?.();
                  }}
                >
                  {t('Manage Favorites')}
                </Button>
              )}
            </div>
          </Accordion.Header>
          <Accordion.Body css={bodyStyles}>
            <Table
              tableInstanceId={accordionId}
              hasResponsiveColWidths
              hasResizeColumns
              colConfig={FREQUENCY_LIMITATION_COLUMNS_CONFIG}
              data={tableData}
              tableStyle={css(tableStyles, customTableStyles)}
              onSortChange={(res) => {
                const { id, value } = res?.[0] || {};
                if (id && value) {
                  handleSorting(id, value);
                }
              }}
            />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  }
);

AccordionTable.displayName = 'AccordionTable';

const headerStyles = css`
  height: 40px;
  padding: ${theme.spacing(0.5)} ${theme.spacing(1)} ${theme.spacing(0.5)} 0;
  background-color: ${theme.colors.neutral5};
  border-left: 1px solid ${theme.colors.neutral20};
  border-right: 1px solid ${theme.colors.neutral20};
  .header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .accordion-header {
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  }
  /* This applies border-radius ONLY to the last Accordion.Header */
  h3:last-of-type > div[class$='header-container'] {
    border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
  }
`;
const bodyStyles = css`
  padding: 0;
  [role='cell'] > span {
    white-space: pre-wrap;
  }
`;

const tableStyles = css`
  border-top: none !important;
  max-height: ${theme.spacing(50)};
  border-radius: 0 !important;
`;

const accordionItemStyles = css`
  box-shadow: none;
  margin-bottom: 0;
  border-radius: 0;
  overflow: visible;
  h3,
  :has(button[aria-expanded='false']) {
    border-bottom: 1px solid ${theme.colors.neutral20};
  }
  h3,
  :has(button[aria-expanded='true']) {
    border-bottom: none;
  }
`;
